import { useEffect, useRef } from "react";
import { trackAdminEvent } from "../../../../services/AdminTelemetryService";

/**
 * Reports core Web Vitals via PerformanceObserver where supported.
 */
export function useWebVitals(): void {
	const sentRef = useRef(false);

	useEffect(() => {
		if (typeof window === "undefined" || !("PerformanceObserver" in window)) return;

		const vitals: Record<string, number | null> = {
			lcp: null,
			fid: null,
			inp: null,
			cls: null,
			ttfb: null,
		};

		try {
			const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
			if (nav) {
				vitals.ttfb = nav.responseStart - nav.requestStart;
			}
		} catch (_) {}

		const trySend = () => {
			if (sentRef.current) return;
			if (vitals.lcp != null || vitals.cls != null || vitals.ttfb != null) {
				sentRef.current = true;
				void trackAdminEvent({
					eventType: "performance",
					level: "info",
					action: "web_vitals",
					metadata: { ...vitals },
				});
			}
		};

		let lcpObs: PerformanceObserver | null = null;
		let clsObs: PerformanceObserver | null = null;
		let fidObs: PerformanceObserver | null = null;

		try {
			lcpObs = new PerformanceObserver((list) => {
				const entries = list.getEntries() as PerformanceEntry[];
				const last = entries[entries.length - 1] as PerformanceEntry & { startTime?: number };
				if (last && typeof last.startTime === "number") {
					vitals.lcp = last.startTime;
				}
				trySend();
			});
			lcpObs.observe({ type: "largest-contentful-paint", buffered: true } as PerformanceObserverInit);
		} catch (_) {}

		try {
			let clsValue = 0;
			clsObs = new PerformanceObserver((list) => {
				for (const e of list.getEntries() as any[]) {
					if (!e.hadRecentInput) clsValue += e.value || 0;
				}
				vitals.cls = clsValue;
				trySend();
			});
			clsObs.observe({ type: "layout-shift", buffered: true } as PerformanceObserverInit);
		} catch (_) {}

		try {
			fidObs = new PerformanceObserver((list) => {
				for (const e of list.getEntries()) {
					const pe = e as any;
					if (pe.processingStart && pe.startTime) {
						vitals.fid = pe.processingStart - pe.startTime;
						break;
					}
				}
				trySend();
			});
			fidObs.observe({ type: "first-input", buffered: true } as PerformanceObserverInit);
		} catch (_) {}

		const t = window.setTimeout(() => trySend(), 8000);
		return () => {
			window.clearTimeout(t);
			lcpObs?.disconnect();
			clsObs?.disconnect();
			fidObs?.disconnect();
		};
	}, []);
}
